import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { createService, getSchedulingErrorMessage, updateService } from "../../../api/schedulingApi";
import { Alert, Field, Modal, btnPrimary, btnSecondary, card, inputClass } from "./ui";

const EMPTY = { name: "", duration_minutes: 30, price: "", discount_percent: "0", is_active: true };

export default function ServicesPanel({ botId, services, onServicesChanged, readOnly = false }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  useEffect(() => {
    if (editing) {
      setForm({ ...EMPTY, ...editing, price: editing.price ?? "", discount_percent: editing.discount_percent ?? "0" });
      setError("");
    }
  }, [editing]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const values = {
      name: form.name.trim(),
      duration_minutes: Number(form.duration_minutes),
      price: form.price === "" ? null : form.price,
      discount_percent: form.discount_percent === "" ? 0 : form.discount_percent,
      is_active: form.is_active,
    };
    try {
      if (editing.id) await updateService(botId, editing.id, values);
      else await createService(botId, values);
      setEditing(null);
      await onServicesChanged();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not save service."));
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className={`${card} space-y-4`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900">Services</h3>
          <p className="text-xs text-slate-500">Optional. Without services, bookings use each provider's slot length.</p>
        </div>
        {!readOnly && <button type="button" className={btnPrimary} onClick={() => setEditing({})}><Plus size={16} /> Add service</button>}
      </div>
      {services.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No services yet.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr><th className="py-2">Name</th><th>Duration</th><th>Price</th><th>Discount</th><th>Status</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {services.map((s) => (
              <tr key={s.id}>
                <td className="py-2 font-semibold text-slate-900">{s.name}</td>
                <td>{s.duration_minutes} min</td>
                <td>{s.price != null ? `₹${s.price}` : "—"}</td>
                <td>{s.discount_percent != null ? `${s.discount_percent}%` : "0%"}</td>
                <td className={s.is_active ? "text-emerald-700" : "text-slate-400"}>{s.is_active ? "Active" : "Inactive"}</td>
                <td className="text-right">{!readOnly && <button type="button" className={btnSecondary} onClick={() => setEditing(s)}>Edit</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!readOnly && <Modal open={editing !== null} title={editing?.id ? "Edit service" : "Add service"} onClose={() => setEditing(null)}>
        <form className="space-y-4" onSubmit={submit}>
          <Alert>{error}</Alert>
          <Field label="Name"><input required className={inputClass} value={form.name} onChange={set("name")} placeholder="Consultation, Haircut…" /></Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Duration (minutes)"><input required type="number" min="5" step="5" className={inputClass} value={form.duration_minutes} onChange={set("duration_minutes")} /></Field>
            <Field label="Price (optional)"><input type="number" min="0" step="0.01" className={inputClass} value={form.price} onChange={set("price")} /></Field>
            <Field label="Discount (%)"><input type="number" min="0" max="100" step="0.01" className={inputClass} value={form.discount_percent} onChange={set("discount_percent")} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.is_active} onChange={set("is_active")} /> Bookable</label>
          <div className="flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
            <button type="submit" className={btnPrimary}>Save</button>
          </div>
        </form>
      </Modal>}
    </div>
  );
}
